/**
 * AD-040 (2026-06-24) — Intake drawing-set WRITE-THROUGH into the linked
 * Property's AD-038 Concept ▸ Version ▸ Floor record.
 *
 * Owner-locked: every intake save of a drawing set writes the set INTO the
 * linked property as a NEW DESIGN CONCEPT (not a version) — so each intake's
 * drawings are a distinct, comparable concept on the building, tagged with
 * provenance (source intake id + tier + date). Forward-only: this fires on
 * save of new/edited intakes; historical intakes are NOT backfilled.
 *
 * Before AD-040 only the property IDENTITY wrote through (`propertyInputToSeed`
 * → createProperty). The architect's actual drawing FILES stayed parked on the
 * intake. This module extends the write-through to carry the drawing set's
 * versions → floors → plans into the property record, where the grid engine +
 * Property page read them.
 *
 * The PURE builder (`buildConceptFromDrawings`) is Firebase-free + React-free so
 * the `.cjs` test runner exercises the concept-shape mapping. The impure writer
 * (`writeDrawingSetThrough`) appends it to the property via propertyStore.
 *
 * Plans are NOT re-uploaded — the intake floors already carry uploaded Storage
 * URLs (`planUrl` / `planStoragePath`), so we construct FloorPlanVersion records
 * that point at the SAME Storage objects (no duplicate upload, no re-fetch).
 */

import type { DrawingsInput, DrawingVersion, FloorInput } from './intake';
import { DRAWING_STAGES } from './intake';
import {
  type DesignConcept,
  type DesignVersion,
  type PropertyFloor,
  type FloorPlanVersion,
  type ConceptSource,
  floorIdFromLabel,
  floorLabel,
  newConceptId,
  newVersionId,
  newPlanId,
} from './propertyModel';

/** Provenance for an intake drawing-set write-through. */
export interface DrawingWriteThroughProvenance {
  /** The intake scope id (projectId when project-scoped, else clientId). */
  sourceIntakeId?: string;
  /** Service tier the intake belongs to (e.g. `L1C`, `L2C_R1`). */
  tier?: string;
  /** Actor uid (audit). */
  actorUid?: string;
  /** ISO timestamp; defaults to now. Injectable for tests. */
  at?: string;
}

/** PURE — map an intake DrawingStage value to the AD-038 DesignVersion stage. */
export function drawingStageToDesignStage(stage: string | undefined): string {
  const s = (stage || '').trim();
  if (!s) return 'Concept';
  // intake stage values: concept | schematic | developed | working | other
  switch (s) {
    case 'concept':
      return 'Concept';
    case 'schematic':
      return 'Preliminary';
    case 'developed':
      return 'Developed';
    case 'working':
      return 'Final';
    default: {
      // Fall back to the human label of the intake stage, else the raw string.
      const label = DRAWING_STAGES.find((x) => x.value === s)?.label;
      return label || s;
    }
  }
}

/** PURE — does a floor row carry any persisted content worth writing through? */
function floorHasContent(f: FloorInput): boolean {
  return Boolean((f.floor || '').trim() || (f.areaApprox || '').trim() || f.planUrl);
}

/** PURE — does a drawing version carry any content worth writing through? */
function versionHasContent(v: DrawingVersion): boolean {
  return Boolean(
    (v.stage || '').trim() ||
      (v.label || '').trim() ||
      (v.floors || []).some(floorHasContent),
  );
}

/**
 * PURE — build a FloorPlanVersion from an intake floor that has an uploaded
 * plan. Points at the SAME Storage object (no re-upload). Dimensions are
 * unknown at intake time (the intake stores only the URL) — set to 0; the
 * grid tracer / report reads natural dimensions from the image on load.
 * Returns null when the floor has no uploaded plan.
 */
export function planVersionFromFloor(
  f: FloorInput,
  at: string,
  stage?: string,
): FloorPlanVersion | null {
  if (!f.planUrl) return null;
  return {
    planId: newPlanId(),
    imageUrl: f.planUrl,
    storagePath: f.planStoragePath || '',
    contentType: f.planMime || 'image/jpeg',
    width: 0,
    height: 0,
    uploadedAt: at,
    isActive: true,
    ...(stage?.trim() ? { stage: stage.trim() } : {}),
  };
}

/**
 * PURE — map one intake floor → a PropertyFloor (with its plan version, if any).
 * Floor id is inferred from the intake's free label.
 */
export function propertyFloorFromIntakeFloor(
  f: FloorInput,
  at: string,
  stage?: string,
): PropertyFloor {
  const floorId = floorIdFromLabel(f.floor);
  const plan = planVersionFromFloor(f, at, stage);
  const areaApprox = (() => {
    const n = Number((f.areaApprox || '').trim());
    return Number.isFinite(n) && n > 0 ? n : undefined;
  })();
  return {
    floorId,
    label: (f.floor || '').trim() || floorLabel(floorId),
    ...(areaApprox !== undefined ? { areaApprox } : {}),
    ...(plan ? { plans: [plan] } : {}),
  };
}

/**
 * PURE — map one intake DrawingVersion → an AD-038 DesignVersion (floors + plans).
 * The first version is active by default; the caller marks exactly one active.
 */
export function designVersionFromDrawingVersion(
  v: DrawingVersion,
  at: string,
  isActive: boolean,
): DesignVersion {
  const stage = drawingStageToDesignStage(v.stage);
  const floors = (v.floors || [])
    .filter(floorHasContent)
    .map((f) => propertyFloorFromIntakeFloor(f, at, stage));
  return {
    versionId: newVersionId(),
    stage,
    isActive,
    createdAt: at,
    floors,
  };
}

/**
 * PURE — turn an intake `DrawingsInput` into a NEW DesignConcept (owner-locked:
 * a new CONCEPT each write-through, not a version). All the drawing-set's
 * versions become the concept's versions (the FIRST is active). The concept
 * carries provenance. `isSelected` is left FALSE here — the caller decides
 * whether to select it (the property store auto-selects the first concept on
 * an empty property; a write-through onto a populated property leaves the prior
 * selection untouched).
 *
 * Returns null when the drawing set has NO content worth writing through (no
 * versions with a stage/label/floor) — so an empty drawings stage is a no-op.
 */
export function buildConceptFromDrawings(
  d: DrawingsInput,
  prov: DrawingWriteThroughProvenance,
): DesignConcept | null {
  const at = prov.at || new Date().toISOString();
  const versionsSrc = (d.versions || []).filter(versionHasContent);
  if (!versionsSrc.length) return null;
  const versions = versionsSrc.map((v, i) =>
    designVersionFromDrawingVersion(v, at, i === 0),
  );
  // Concept name: prefer a meaningful tier/date label.
  const tierBit = (prov.tier || '').replace(/_R\d+$/i, '').toUpperCase();
  const dateBit = at.slice(0, 10);
  const name = tierBit ? `${tierBit} intake · ${dateBit}` : `Intake drawings · ${dateBit}`;
  const source: ConceptSource = {
    kind: 'intake',
    at,
    ...(prov.sourceIntakeId ? { sourceIntakeId: prov.sourceIntakeId } : {}),
    ...(tierBit ? { tier: tierBit } : {}),
    ...(prov.actorUid ? { by: prov.actorUid } : {}),
  };
  return {
    conceptId: newConceptId(),
    name,
    isSelected: false,
    createdAt: at,
    versions,
    source,
  };
}
